import React, { forwardRef } from 'react';
import type { ComponentType } from 'react';

import { expValEquals } from '@confluence/feature-experiments';

import { isReportedReactLazyId } from './loadableGlobals.ts';
import { addLoadableId, preloadIfNeeded } from './preloader.ts';

type LazyModule = { default: ComponentType<any> };

// React 18's lazy initializer checks the component's status as soon as `then` returns, and suspends
// unless `then` has already called back. `Promise.resolve(loaded)` always calls back in a
// microtask, so it would suspend even for a loaded module. This Promise overrides `then` to call
// back synchronously. The rest of the Promise API goes through `then`, so `catch`, `finally` and
// `Promise.all` still work.
const resolvedThenable = <T,>(value: T): Promise<T> => {
	const promise = new Promise<T>((resolve) => resolve(value));
	promise.then = ((onFulfilled?: (value: T) => unknown) =>
		Promise.resolve(onFulfilled ? onFulfilled(value) : value)) as typeof promise.then;
	return promise;
};

function withServerTracking<M extends LazyModule>(id: string, module: M): M {
	const Original = module.default;

	const TrackedLazy = forwardRef<unknown, Record<string, unknown>>((props, ref) => {
		// Effects do not run during SSR, so render is the only place to record the rendered split.
		if (expValEquals('confluence_ssr_react_lazy_preload_v2', 'isEnabled', true)) {
			addLoadableId(id);
		}
		return <Original {...props} ref={ref} />;
	});
	TrackedLazy.displayName = `TrackedLazy(${Original.displayName || Original.name || 'Component'})`;

	return { ...module, default: TrackedLazy };
}

/**
 * Wraps a `React.lazy` loader so SSR can report the code split it rendered and the client can load
 * that split before hydration. LoadableReactLazyBabelPlugin injects the calls; do not call it
 * directly.
 */
export function trackReactLazy<M extends LazyModule>(
	id: string,
	loader: () => Promise<M>,
): () => Promise<M> {
	// process.env.REACT_SSR is read inline so each build can drop the other side's branch. None of
	// these branches changes rendered markup, so they cannot cause a hydration mismatch.
	const prepare = (module: M): M =>
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		process.env.REACT_SSR ? withServerTracking(id, module) : module;

	let loaded: M | undefined;
	let pending: Promise<M> | undefined;
	const load = (): Promise<M> => {
		pending ??= loader().then(
			(module) => {
				loaded = prepare(module);
				return loaded;
			},
			(error) => {
				pending = undefined;
				throw error;
			},
		);
		return pending;
	};

	let preloaded = false;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR && isReportedReactLazyId(id)) {
		// Waiting a microtask lets the defining module finish evaluating before the loader reads any
		// of its bindings.
		preloadIfNeeded(id, undefined, () =>
			Promise.resolve()
				.then(load)
				.then(() => {
					preloaded = true;
				}),
		);
	}

	return () => {
		if (!loaded) {
			return load();
		}
		// The client only answers synchronously after the preload the server asked for, so lazy
		// components behave as before when the experiment is off.
		const canResolveSynchronously =
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			process.env.REACT_SSR
				? expValEquals('confluence_ssr_react_lazy_preload_v2', 'isEnabled', true)
				: preloaded;
		return canResolveSynchronously ? resolvedThenable(loaded) : load();
	};
}
